import { useState, useEffect, useRef } from "react";
import { sbCreateLead } from "../utils/supabase";
import { sendLeadNotification } from "../utils/emailService";

// ── BC Rent Increase Rates ──────────────────────────────────────────────────
const BC_RATES = [
  { year: 2026, rate: 3.0 },
  { year: 2025, rate: 3.0 },
  { year: 2024, rate: 3.5 },
  { year: 2023, rate: 2.0 },
  { year: 2022, rate: 1.5 },
  { year: 2021, rate: 0, note: "COVID freeze" },
];

const fmtC = (n) =>
  "$" + n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const calcEffectiveDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 3);
  d.setDate(1);
  d.setMonth(d.getMonth() + 1);
  return d.toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" });
};

// ── Styles ──────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    background: "#0A0A0D",
    color: "#E8E4DF",
    fontFamily: "'DM Sans', -apple-system, sans-serif",
    WebkitFontSmoothing: "antialiased",
  },
  hero: {
    position: "relative", padding: "60px 24px 40px", textAlign: "center", overflow: "hidden",
  },
  heroGlow: {
    position: "absolute", top: -200, left: "50%", transform: "translateX(-50%)",
    width: 800, height: 800, background: "radial-gradient(ellipse, #E8622A30 0%, transparent 70%)",
    pointerEvents: "none", opacity: 0.6,
  },
  logo: {
    fontSize: 28, fontWeight: 900, letterSpacing: -1, marginBottom: 32, cursor: "pointer",
  },
  badge: {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "#22AA5520", border: "1px solid #22AA5530", borderRadius: 99,
    padding: "6px 16px 6px 12px", fontSize: 12, fontWeight: 700, color: "#22AA55", marginBottom: 20,
  },
  badgeDot: {
    width: 8, height: 8, background: "#22AA55", borderRadius: "50%", flexShrink: 0,
  },
  h1: {
    fontSize: "clamp(32px, 5vw, 52px)", fontWeight: 900, lineHeight: 1.1,
    letterSpacing: -1.5, marginBottom: 16,
  },
  subtitle: {
    fontSize: 17, color: "#9993A8", maxWidth: 520, margin: "0 auto 8px", lineHeight: 1.6,
  },
  bcFlag: { fontSize: 13, color: "#5A5570", marginTop: 12 },
  wrap: { maxWidth: 880, margin: "0 auto", padding: "0 24px 80px" },
  card: {
    background: "#111116", border: "1px solid #ffffff0a", borderRadius: 20, overflow: "hidden",
  },
  rateBanner: {
    background: "linear-gradient(135deg, #0d1a0f, #0a160d)",
    borderBottom: "1px solid #22AA5515", padding: "28px 32px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexWrap: "wrap", gap: 16,
  },
  rateLeft: { display: "flex", alignItems: "center", gap: 16 },
  rateCircle: {
    width: 64, height: 64, borderRadius: "50%", border: "3px solid #22AA55",
    display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column",
  },
  rateNum: { fontSize: 22, fontWeight: 900, color: "#22AA55", lineHeight: 1 },
  ratePct: { fontSize: 10, color: "#22AA55", fontWeight: 700 },
  rateInfo: { fontSize: 12, color: "#5A5570", maxWidth: 280, lineHeight: 1.6 },
  formBody: { padding: 32 },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 },
  label: {
    display: "block", fontSize: 11, fontWeight: 700, color: "#5A5570",
    textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8,
  },
  input: {
    width: "100%", background: "#0A0A0D", border: "1px solid #ffffff12", borderRadius: 12,
    padding: "14px 16px", color: "#E8E4DF", fontSize: 15, fontFamily: "inherit", outline: "none",
    boxSizing: "border-box",
  },
  select: {
    width: "100%", background: "#0A0A0D", border: "1px solid #ffffff12", borderRadius: 12,
    padding: "14px 16px", color: "#E8E4DF", fontSize: 15, fontFamily: "inherit", outline: "none",
    boxSizing: "border-box",
  },
  hint: { fontSize: 11, color: "#5A5570", marginTop: 6 },
  warn: { fontSize: 11, color: "#FF2D2D", marginTop: 6 },
  sliderRow: { display: "flex", alignItems: "center", gap: 16, gridColumn: "1 / -1" },
  rateVal: { fontSize: 28, fontWeight: 900, color: "#E8622A", minWidth: 70, textAlign: "right" },
  results: {
    background: "#0A0A0D", border: "1px solid #ffffff0a", borderRadius: 16, padding: 28, marginTop: 8,
  },
  resultsGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24,
  },
  resultItem: {
    textAlign: "center", padding: "20px 16px", background: "#111116",
    borderRadius: 14, border: "1px solid #ffffff0a",
  },
  resultLabel: {
    fontSize: 10, fontWeight: 700, color: "#5A5570",
    textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8,
  },
  resultValue: { fontSize: 32, fontWeight: 900, letterSpacing: -1 },
  resultSub: { fontSize: 11, color: "#5A5570", marginTop: 4 },
  effectiveStrip: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: 16,
    background: "#E8622A08", border: "1px solid #E8622A18", borderRadius: 12, marginBottom: 24,
  },
  cta: {
    borderTop: "1px solid #ffffff0a", padding: 32,
    background: "linear-gradient(180deg, #111116, #0e0e13)",
  },
  ctaInner: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, alignItems: "center",
  },
  ctaH3: { fontSize: 22, fontWeight: 900, letterSpacing: -.5, marginBottom: 8 },
  ctaP: { fontSize: 13, color: "#9993A8", lineHeight: 1.7 },
  ctaFeature: { display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#9993A8" },
  check: {
    width: 20, height: 20, borderRadius: 6, background: "#22AA5520", color: "#22AA55",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, flexShrink: 0,
  },
  ctaInput: {
    width: "100%", background: "#0A0A0D", border: "1px solid #ffffff12", borderRadius: 12,
    padding: "14px 16px", color: "#E8E4DF", fontSize: 14, fontFamily: "inherit", outline: "none",
    boxSizing: "border-box",
  },
  ctaBtn: {
    width: "100%", padding: 16, border: "none", borderRadius: 12,
    background: "#E8622A", color: "#fff", fontSize: 15, fontWeight: 700,
    fontFamily: "inherit", cursor: "pointer", letterSpacing: -.2,
  },
  ctaBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  ctaSmall: { fontSize: 11, color: "#5A5570", textAlign: "center", marginTop: 12 },
  infoGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20,
    maxWidth: 880, margin: "0 auto", padding: "0 24px 80px",
  },
  infoCard: {
    background: "#111116", border: "1px solid #ffffff0a", borderRadius: 16, padding: 24,
  },
  footer: {
    textAlign: "center", padding: "40px 24px", fontSize: 12, color: "#5A5570",
    borderTop: "1px solid #ffffff0a",
  },
};

// ── Component ───────────────────────────────────────────────────────────────
const RentCalculator = ({ onBack }) => {
  const [currentRent, setCurrentRent] = useState("");
  const [yearIdx, setYearIdx] = useState(0);
  const [rateValue, setRateValue] = useState(3.0);
  const [ctaName, setCtaName] = useState("");
  const [ctaEmail, setCtaEmail] = useState("");
  const [ctaProps, setCtaProps] = useState("");
  const [ctaSubmitted, setCtaSubmitted] = useState(false);
  const [ctaSubmitting, setCtaSubmitting] = useState(false);
  const rentRef = useRef(null);

  const maxRate = BC_RATES[yearIdx].rate;
  const effectiveRate = Math.min(rateValue, maxRate);
  const rent = parseFloat(currentRent) || 0;
  const increase = rent * (effectiveRate / 100);
  const newRent = rent + increase;
  const showResults = rent > 0;

  useEffect(() => {
    if (rateValue > maxRate) setRateValue(maxRate);
  }, [yearIdx]);

  useEffect(() => {
    // Set page URL without full navigation
    if (window.history.replaceState) {
      window.history.replaceState(null, "", "/calculator");
    }
  }, []);

  const handleCtaSubmit = async () => {
    if (!ctaEmail || !ctaEmail.includes("@")) return;
    setCtaSubmitting(true);

    const leadData = {
      name: ctaName,
      email: ctaEmail,
      properties: ctaProps,
      source: "calculator",
      currentRent: currentRent || null,
    };

    try {
      // 1. Save to Supabase leads table
      await sbCreateLead(leadData);
    } catch (err) {
      console.error("Lead save failed:", err);
      // Still continue — don't block the user experience
    }

    // 2. Email notification to you (non-blocking)
    sendLeadNotification(leadData).catch(() => {});

    setCtaSubmitting(false);
    setCtaSubmitted(true);
  };

  return (
    <div style={S.page}>

      {/* ── Hero ── */}
      <section style={S.hero}>
        <div style={S.heroGlow} />
        <div style={S.logo} onClick={onBack}>
          prop<span style={{ color: "#E8622A" }}>desk</span>
        </div>
        <div style={S.badge}>
          <span style={S.badgeDot} />
          BC Maximum Rent Increase — Updated for 2026
        </div>
        <h1 style={S.h1}>
          Know Your <span style={{ color: "#E8622A" }}>Maximum</span>
          <br />Rent Increase
        </h1>
        <p style={S.subtitle}>
          Free calculator for BC landlords. Get your exact numbers in seconds,
          fully compliant with the Residential Tenancy Act.
        </p>
        <div style={S.bcFlag}>🍁 British Columbia, Canada</div>
      </section>

      {/* ── Calculator Card ── */}
      <section style={S.wrap}>
        <div style={S.card}>

          {/* Rate banner */}
          <div style={S.rateBanner}>
            <div style={S.rateLeft}>
              <div style={S.rateCircle}>
                <div style={S.rateNum}>{BC_RATES[yearIdx].rate.toFixed(1)}</div>
                <div style={S.ratePct}>%</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#22AA55", textTransform: "uppercase", letterSpacing: ".08em" }}>
                  {BC_RATES[yearIdx].year} Maximum Allowable Increase
                </div>
                <div style={{ fontSize: 13, color: "#9993A8", marginTop: 2 }}>
                  Set by the Province of British Columbia
                </div>
              </div>
            </div>
            <div style={S.rateInfo}>
              Must give 3 full months notice. See{" "}
              <a href="https://www.gov.bc.ca/landlordtenant" target="_blank" rel="noreferrer" style={{ color: "#22AA55", textDecoration: "none" }}>
                RTB Info →
              </a>
            </div>
          </div>

          {/* Form */}
          <div style={S.formBody}>
            <div style={S.formGrid}>
              <div>
                <label style={S.label}>Current Monthly Rent</label>
                <input
                  ref={rentRef}
                  style={S.input}
                  type="number"
                  placeholder="e.g. 2200"
                  value={currentRent}
                  onChange={(e) => setCurrentRent(e.target.value)}
                />
                <div style={S.hint}>The rent your tenant currently pays per month</div>
              </div>
              <div>
                <label style={S.label}>Province Year</label>
                <select
                  style={S.select}
                  value={yearIdx}
                  onChange={(e) => setYearIdx(parseInt(e.target.value))}
                >
                  {BC_RATES.filter((r) => r.rate > 0).map((r, i) => (
                    <option key={r.year} value={i}>
                      {r.year} — {r.rate}% maximum
                    </option>
                  ))}
                </select>
                <div style={S.hint}>Select the year for your rent increase</div>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={S.label}>Your Increase Rate</label>
                <div style={S.sliderRow}>
                  <input
                    type="range"
                    min="0"
                    max={maxRate}
                    step="0.1"
                    value={rateValue}
                    onChange={(e) => setRateValue(parseFloat(e.target.value))}
                    style={{ flex: 1, accentColor: "#E8622A" }}
                  />
                  <div style={{ ...S.rateVal, color: rateValue > maxRate ? "#FF2D2D" : "#E8622A" }}>
                    {rateValue.toFixed(1)}%
                  </div>
                </div>
                <div style={rateValue > maxRate ? S.warn : S.hint}>
                  {rateValue > maxRate
                    ? `⚠ Exceeds ${maxRate}% maximum — will be capped`
                    : `You can increase up to the maximum of ${maxRate}%`}
                </div>
              </div>
            </div>

            {/* Results */}
            {showResults && (
              <div style={S.results}>
                <div style={S.resultsGrid}>
                  <div style={S.resultItem}>
                    <div style={S.resultLabel}>Current Rent</div>
                    <div style={{ ...S.resultValue, color: "#9993A8" }}>{fmtC(rent)}</div>
                    <div style={S.resultSub}>per month</div>
                  </div>
                  <div style={S.resultItem}>
                    <div style={S.resultLabel}>Increase Amount</div>
                    <div style={{ ...S.resultValue, color: "#E8622A" }}>{fmtC(increase)}</div>
                    <div style={S.resultSub}>{effectiveRate.toFixed(1)}% increase</div>
                  </div>
                  <div style={S.resultItem}>
                    <div style={S.resultLabel}>New Monthly Rent</div>
                    <div style={{ ...S.resultValue, color: "#22AA55" }}>{fmtC(newRent)}</div>
                    <div style={S.resultSub}>{fmtC(newRent * 12)} / year</div>
                  </div>
                </div>

                <div style={S.effectiveStrip}>
                  <span style={{ fontSize: 20 }}>📅</span>
                  <span style={{ fontSize: 13, color: "#9993A8" }}>
                    Earliest effective date if served today:{" "}
                    <strong style={{ color: "#E8E4DF" }}>{calcEffectiveDate()}</strong>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* CTA Section */}
          <div style={S.cta}>
            <div style={S.ctaInner}>
              <div>
                <h3 style={S.ctaH3}>
                  Generate your <span style={{ color: "#E8622A" }}>RTB-7 notice</span> automatically
                </h3>
                <p style={S.ctaP}>
                  PropDesk generates BC-compliant rent increase notices with e-signature
                  and email delivery — plus 5 more RTB form types, maintenance tracking,
                  and tenant self-service portals.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
                  {[
                    "RTB-7, RTB-30, RTB-32 notice generation",
                    "Condition inspection reports",
                    "Tenant portal with maintenance requests",
                    "Owner invoicing with e-Transfer",
                    "Built for Vancouver property managers",
                  ].map((f) => (
                    <div key={f} style={S.ctaFeature}>
                      <span style={S.check}>✓</span> {f}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                {ctaSubmitted ? (
                  <div style={{ textAlign: "center", padding: 20 }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#22AA55", marginBottom: 4 }}>
                      You're on the list{ctaName ? `, ${ctaName}` : ""}!
                    </div>
                    <div style={{ fontSize: 13, color: "#9993A8" }}>
                      We'll reach out shortly with access to PropDesk.
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <input
                      style={S.ctaInput}
                      placeholder="Your name"
                      value={ctaName}
                      onChange={(e) => setCtaName(e.target.value)}
                    />
                    <input
                      style={S.ctaInput}
                      type="email"
                      placeholder="Email address"
                      value={ctaEmail}
                      onChange={(e) => setCtaEmail(e.target.value)}
                    />
                    <input
                      style={S.ctaInput}
                      placeholder="How many rental properties? (e.g. 3)"
                      value={ctaProps}
                      onChange={(e) => setCtaProps(e.target.value)}
                    />
                    <button
                      style={{ ...S.ctaBtn, ...(ctaSubmitting ? S.ctaBtnDisabled : {}) }}
                      onClick={handleCtaSubmit}
                      disabled={ctaSubmitting}
                    >
                      {ctaSubmitting ? "Submitting..." : "Get Early Access to PropDesk"}
                    </button>
                    <div style={S.ctaSmall}>
                      No credit card required.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── Info Cards ── */}
      <section style={S.infoGrid}>
        <div style={S.infoCard}>
          <div style={{ fontSize: 24, marginBottom: 12 }}>📋</div>
          <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>3 Full Months Notice Required</h4>
          <p style={{ fontSize: 13, color: "#9993A8", lineHeight: 1.7 }}>
            Landlords must provide at least 3 full calendar months notice before a rent increase
            takes effect. The increase must take effect on the first day of a month.
          </p>
        </div>
        <div style={S.infoCard}>
          <div style={{ fontSize: 24, marginBottom: 12 }}>🔒</div>
          <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Once Per 12 Months Only</h4>
          <p style={{ fontSize: 13, color: "#9993A8", lineHeight: 1.7 }}>
            A landlord can only increase rent once every 12 months. The increase must follow
            the prescribed form (RTB-7) to be valid.
          </p>
        </div>
        <div style={S.infoCard}>
          <div style={{ fontSize: 24, marginBottom: 12 }}>📊</div>
          <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Historical BC Rates</h4>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
            <thead>
              <tr>
                {["Year", "Max Increase"].map((h) => (
                  <th key={h} style={{ fontSize: 10, fontWeight: 700, color: "#5A5570", textTransform: "uppercase", letterSpacing: ".08em", padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #ffffff0a" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BC_RATES.map((r) => (
                <tr key={r.year}>
                  <td style={{ fontSize: 13, padding: "8px 12px", borderBottom: "1px solid #ffffff06", color: r.year === 2026 ? "#E8E4DF" : "#9993A8" }}>
                    {r.year}
                  </td>
                  <td style={{ fontSize: 13, padding: "8px 12px", borderBottom: "1px solid #ffffff06", fontWeight: 700, color: "#22AA55" }}>
                    {r.rate > 0 ? `${r.rate}%` : `0% ${r.note || ""}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={S.infoCard}>
          <div style={{ fontSize: 24, marginBottom: 12 }}>⚖️</div>
          <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Tenant Can Dispute</h4>
          <p style={{ fontSize: 13, color: "#9993A8", lineHeight: 1.7 }}>
            If a tenant believes the rent increase exceeds the allowable amount or proper notice
            wasn't given, they can apply for dispute resolution through the Residential Tenancy
            Branch within 30 days.
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={S.footer}>
        <div style={{ ...S.logo, fontSize: 20, marginBottom: 12, cursor: "pointer" }} onClick={onBack}>
          prop<span style={{ color: "#E8622A" }}>desk</span>
        </div>
        <div>Property Management Software for Vancouver</div>
        <div style={{ marginTop: 8 }}>
          This calculator is for informational purposes. Verify with the{" "}
          <a href="https://www.gov.bc.ca/landlordtenant" target="_blank" rel="noreferrer" style={{ color: "#E8622A", textDecoration: "none" }}>
            BC Residential Tenancy Branch
          </a>.
        </div>
      </footer>
    </div>
  );
};

export default RentCalculator;
